import { useEffect, useRef, useState } from 'react';

export interface QuizTimerProps {
  /** Server-side deadline for the attempt. */
  expiresAt: Date;
  /** The server's own clock at the moment the attempt was started/resumed. */
  serverNow: Date;
  /** The client's clock at that same moment, used to measure skew. */
  clientNowAtStart: Date;
  /**
   * Fired when the countdown reaches zero. Return (or resolve to) `false` to
   * signal a transient failure — e.g. the auto-submit network call failed —
   * and the timer will retry a bounded number of times with backoff. Any
   * other return value (including `undefined`/`void`) is treated as settled.
   */
  onExpire: () => void | boolean | Promise<void | boolean>;
  className?: string;
}

const TICK_MS = 250;
/** Auto-submit-on-expiry retry bound: a network blip should not require the
 *  student to notice and click Submit themselves, but an endless retry loop
 *  against a permanently-down backend is not useful either. */
const MAX_EXPIRE_RETRIES = 5;
const EXPIRE_RETRY_BASE_MS = 3000;

/** Seconds under which the timer turns amber. */
export const WARN_SECONDS = 120;
/** Seconds under which the timer turns red. */
export const DANGER_SECONDS = 30;

/**
 * remainingSeconds derives the time left from the SERVER's deadline, corrected
 * by the clock skew measured when the attempt started. It deliberately never
 * counts down from a client-side start timestamp: a laptop that sleeps, or a
 * system clock that is simply wrong, must not hand a student extra minutes.
 * The server rejects late autosaves and late submits regardless, so a timer
 * that drifts optimistically would only lose the student their answers.
 */
export function remainingSeconds(expiresAt: Date, skewMs: number, clientNowMs: number): number {
  const serverNowMs = clientNowMs - skewMs;
  return Math.max(0, Math.ceil((expiresAt.getTime() - serverNowMs) / 1000));
}

export function formatClock(totalSeconds: number): string {
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

export function QuizTimer({ expiresAt, serverNow, clientNowAtStart, onExpire, className }: QuizTimerProps) {
  // Skew is measured once, at start. Re-measuring it later would let a client
  // clock that drifts mid-attempt move the deadline.
  const skewRef = useRef(clientNowAtStart.getTime() - serverNow.getTime());
  const expiresMs = expiresAt.getTime();

  const [left, setLeft] = useState(() => remainingSeconds(expiresAt, skewRef.current, Date.now()));

  // onExpire is held in a ref so a re-created callback does not restart the
  // interval, and firedRef guarantees it fires exactly once per attempt.
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;
  const firedRef = useRef(false);
  // Counts failed expiry attempts so retries are bounded, not infinite.
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    firedRef.current = false;
    retryCountRef.current = 0;
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, [expiresMs]);

  useEffect(() => {
    // A failure (network blip on the auto-submit call) must not strand the
    // student on an expired timer forever: unset firedRef after a backoff so
    // the next tick re-fires, up to MAX_EXPIRE_RETRIES times.
    const scheduleRetry = () => {
      if (retryCountRef.current >= MAX_EXPIRE_RETRIES) return;
      retryCountRef.current += 1;
      const delay = EXPIRE_RETRY_BASE_MS * retryCountRef.current;
      retryTimerRef.current = setTimeout(() => {
        firedRef.current = false;
      }, delay);
    };
    const fireExpire = () => {
      firedRef.current = true;
      // Call onExpire synchronously (not deferred to a microtask) so a
      // synchronous mock/callback fires within the same tick, exactly as
      // before this retry logic was added; only its settlement is async.
      let ret: void | boolean | Promise<void | boolean>;
      try {
        ret = onExpireRef.current();
      } catch {
        scheduleRetry();
        return;
      }
      Promise.resolve(ret).then((settled) => {
        if (settled === false) scheduleRetry();
      }).catch(() => scheduleRetry());
    };
    const tick = () => {
      const next = remainingSeconds(new Date(expiresMs), skewRef.current, Date.now());
      setLeft(next);
      if (next <= 0 && !firedRef.current) fireExpire();
    };
    tick();
    const id = setInterval(tick, TICK_MS);
    return () => {
      clearInterval(id);
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, [expiresMs]);

  const tone =
    left <= DANGER_SECONDS
      ? 'text-red-600 dark:text-red-400 border-red-500/40 bg-red-500/10'
      : left <= WARN_SECONDS
        ? 'text-amber-600 dark:text-amber-400 border-amber-500/40 bg-amber-500/10'
        : 'text-foreground border-border bg-card';

  return (
    <div
      role="timer"
      aria-live={left <= WARN_SECONDS ? 'polite' : 'off'}
      aria-label="Time remaining"
      className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 font-mono text-base font-semibold tabular-nums ${tone} ${className ?? ''}`}
    >
      <span aria-hidden="true">⏱</span>
      {formatClock(left)}
    </div>
  );
}
